import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Ban, CheckCircle2 } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Administrateur" },
  { value: "responsable_commercial", label: "Responsable commercial" },
  { value: "commercial", label: "Commercial" },
  { value: "operateur_validation", label: "Opérateur de validation" },
  { value: "lecture_seule", label: "Lecture seule" },
];

export default function UsersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "commercial" });

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", password: "", role: "commercial" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/users/${id}`, { role });
      toast.success("Rôle mis à jour");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Compte désactivé" : "Compte réactivé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.email} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">Utilisateurs</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAdmin ? "Gérez les membres et leurs droits d'accès." : "Seul un administrateur peut gérer les utilisateurs."}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button"><Plus size={16} className="mr-1.5" /> Ajouter</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
              <form onSubmit={createUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Prénom</Label><Input required className="mt-1.5" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="user-firstname-input" /></div>
                  <div><Label>Nom</Label><Input required className="mt-1.5" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="user-lastname-input" /></div>
                </div>
                <div><Label>E-mail</Label><Input type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
                <div><Label>Mot de passe provisoire (8 car. min.)</Label><Input type="password" required className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" data-testid="user-submit-button">Créer l'utilisateur</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="divide-y" data-testid="users-list">
            {users.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {u.first_name} {u.last_name} {u.id === user.id && <span className="text-xs text-muted-foreground">(vous)</span>}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                </div>
                {!u.is_active && <Badge variant="destructive">Désactivé</Badge>}
                {isAdmin && u.id !== user.id ? (
                  <div className="flex items-center gap-2">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="w-[190px]" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" title={u.is_active ? "Désactiver" : "Réactiver"} onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`}>
                      {u.is_active ? <Ban size={16} /> : <CheckCircle2 size={16} className="text-green-600" />}
                    </Button>
                    <Button variant="ghost" size="icon" title="Supprimer" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}>
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </div>
                ) : (
                  <Badge variant="secondary">{u.role_label}</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
